// ActionListEditor.js
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  AssignmentTurnedIn as FollowUpIcon,
  Description as FileIcon,
  Chat as CommunicateIcon,
  Warning as EscalateIcon,
  EventNote as DiaryIcon,
} from "@mui/icons-material";

const MAX_ALLOWED_AGE = 150;
// Action node configurations
const ACTION_COLORS = {
  "Arrange Monitoring or Follow up": {
    bg: "#e8f5e9", // soft green
    border: "#a5d6a7",
    icon: "#2e7d32",
    hoverBg: "#c8e6c9",
    hoverBorder: "#81c784",
  },
  "File Report": {
    bg: "#e3f2fd", // soft blue
    border: "#90caf9",
    icon: "#1565c0",
    hoverBg: "#bbdefb",
    hoverBorder: "#64b5f6",
  },
  "Communicate with Patient": {
    bg: "#fff8e1", // soft amber
    border: "#ffe082",
    icon: "#ef6c00",
    hoverBg: "#ffecb3",
    hoverBorder: "#ffd54f",
  },
  "Escalate or Urgent": {
    bg: "#ffebee", // soft red (kept for when it's re-enabled)
    border: "#ef9a9a",
    icon: "#c62828",
    hoverBg: "#ffcdd2",
    hoverBorder: "#e57373",
  },
  "Diary Entry": {
    bg: "#f3e5f5", // soft purple
    border: "#ce93d8",
    icon: "#6a1b9a",
    hoverBg: "#e1bee7",
    hoverBorder: "#ba68c8",
  },
};

const ACTION_CONFIGS = {
  "Arrange Monitoring or Follow up": {
    allowMultiple: true,
    template: (values) =>
      values.info_var
        ? `Leave for GP. Info: ${values.info_var}`
        : "Leave for GP",
    fields_config: [
      {
        label: "Task type:",
        var: "task_type",
        widget: "TextField",
        type: "string",
        width: 300,
        required: true,
      },
      {
        label: "Due Date:",
        var: "due_date",
        widget: "TextField",
        type: "date",
        width: 300,
        required: true,
      },
      {
        label: "Notes:",
        var: "notes",
        widget: "TextField",
        type: "string",
        width: 300,
        required: true,
      },
      {
        label: "Assign to:",
        var: "clinician_name",
        widget: "TextField",
        type: "string",
        width: 300,
        required: true,
      },
    ],
    validate: () => ({}),
  },
  "File Report": {
    template: (values) => {
      if (values.filing_approach === "No Comments")
        return "File report without comments";
      if (values.filing_comment)
        return `File report with comment: "${values.filing_comment}"`;
      return "File report";
    },
    fields_config: [
      {
        label: "Filing Approach:",
        var: "filing_approach",
        widget: "RadioButton",
        type: "text",
        values: ["With Comment", "No Comments"],
        required: true,
      },
      {
        label: "Filing Comment:",
        var: "filing_comment",
        widget: "TextField",
        type: "string",
        width: 300,
        visibleWhen: (values) => values.filing_approach === "With Comment",
        required: true,
      },
    ],
    validate: () => ({}),
  },
  "Communicate with Patient": {
    template: (values) =>
      values.accurx_message
        ? `Communicate with Patient: "${values.accurx_message}"`
        : "Communicate with Patient",
    fields_config: [
      {
        label: "Message:",
        var: "accurx_message",
        widget: "TextField",
        type: "string",
        width: 300,
        required: true,
      },
    ],
    validate: () => ({}),
  },
  "Diary Entry": {
    allowMultiple: true,
    template: (values) => {
      const when = !isBlank(values.diary_date)
        ? `on ${values.diary_date}`
        : !isBlank(values.diary_age)
          ? `at patient age ${values.diary_age}`
          : "";
      return [
        "Add diary entry",
        values.code && `code "${values.code}"`,
        when,
        values.hcp && `for HCP ${values.hcp}`,
      ]
        .filter(Boolean)
        .join(", ");
    },
    fields_config: [
      {
        label: "Code:",
        var: "code",
        widget: "TextField",
        type: "string",
        width: 300,
        required: true,
      },
      {
        label: "Diary Date:",
        var: "diary_date",
        widget: "TextField",
        type: "date",
        width: 300,
      },
      {
        label: "Patient Age:",
        var: "diary_age",
        widget: "TextField",
        type: "int",
        width: 300,
      },
      {
        label: "HCP:",
        var: "hcp",
        widget: "TextField",
        type: "string",
        width: 300,
        required: true,
      },
      {
        label: "Diary Text:",
        var: "diary_text",
        widget: "TextArea",
        type: "string",
        width: 620,
        rows: 4,
        required: true,
      },
    ],
    // Required fields are handled by validateAction; this adds the
    // "exactly one of Diary Date / Patient Age" rule.
    validate: (values) => {
      const errs = {};
      const dateStr = String(values.diary_date ?? "").trim();
      const ageStr = String(values.diary_age ?? "").trim();

      if (!dateStr && !ageStr) {
        const msg = "Specify either a Diary Date or a Patient Age";
        errs.diary_date = msg;
        errs.diary_age = msg;
      } else if (dateStr && ageStr) {
        const msg = "Specify only one of Diary Date or Patient Age";
        errs.diary_date = msg;
        errs.diary_age = msg;
      } else if (dateStr) {
        if (
          !/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ||
          Number.isNaN(new Date(`${dateStr}T00:00:00`).getTime())
        ) {
          errs.diary_date = "Diary Date must be a valid date";
        }
      } else if (!/^\d+$/.test(ageStr)) {
        errs.diary_age = "Patient Age must be a non-negative whole number";
      } else if (parseInt(ageStr, 10) > MAX_ALLOWED_AGE) {
        errs.diary_age = `Patient Age cannot exceed ${MAX_ALLOWED_AGE}`;
      }
      return errs;
    },
  },
  // "Escalate or Urgent": {
  //   template: (values) =>
  //     values.new_owner ? `Change Owner to ${values.new_owner}` : "Change Owner",
  //   fields_config: [
  //     {
  //       label: "Change Owner To:",
  //       var: "new_owner",
  //       widget: "TextField",
  //       type: "string",
  //       width: 200,
  //     },
  //   ],
  //   validate: () => ({}),
  // },
};

const AVAILABLE_ACTIONS = Object.keys(ACTION_CONFIGS);

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";

// Validates required, visible fields, then any custom validate() on the config
const validateAction = (config, values = {}) => {
  const errs = {};
  (config.fields_config || []).forEach((field) => {
    if (field.visibleWhen && !field.visibleWhen(values)) return;
    if (field.required && isBlank(values[field.var])) {
      errs[field.var] =
        field.widget === "RadioButton" ? "Please select an option" : "Required";
    }
  });
  return { ...errs, ...(config.validate ? config.validate(values) : {}) };
};

/**
 * ActionListEditor Component
 *
 * Props:
 * - actions: array of action nodes (initial state)
 * - onChange: callback when actions list changes
 * - readOnly: boolean to disable editing
 */
const ActionListEditor = ({
  actions = [],
  onChange = () => {},
  readOnly = false,
  onValidityChange = () => {},
  showAllErrors = false,
}) => {
  const [actionList, setActionList] = useState(actions);
  const [touched, setTouched] = useState({});
  // const [selectedActionType, setSelectedActionType] = useState(
  //   AVAILABLE_ACTIONS[0],
  // );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  // Update parent when local state changes
  useEffect(() => {
    onChange(actionList);
  }, [actionList, onChange]);

  // Validate all actions
  const errors = useMemo(() => {
    const newErrors = {};
    actionList.forEach((action) => {
      const config = ACTION_CONFIGS[action.node];
      if (!config) return;
      const fieldErrors = validateAction(config, action.fields || {});
      if (Object.keys(fieldErrors).length > 0) {
        newErrors[action.id] = fieldErrors;
      }
    });
    return newErrors;
  }, [actionList]);

  // Tell the parent whether the list is currently valid
  useEffect(() => {
    onValidityChange(Object.keys(errors).length === 0);
  }, [errors, onValidityChange]);

  // const addAction = () => {
  //   const newAction = {
  //     id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  //     node: selectedActionType,
  //     fields: {},
  //   };
  //   setActionList([...actionList, newAction]);
  // };

  const markTouched = (actionId, field) =>
    setTouched((prev) => ({ ...prev, [`${actionId}.${field}`]: true }));

  const updateActionField = (actionId, field, value) => {
    markTouched(actionId, field);
    setActionList(
      actionList.map((action) =>
        action.id === actionId
          ? {
              ...action,

              fields: {
                ...action.fields,
                [field]: value,
                ...(field === "filing_approach" && value === "No Comments"
                  ? { filing_comment: "" }
                  : {}),
              },
            }
          : action,
      ),
    );
  };

  const deleteAction = (actionId) => {
    if (window.confirm("Are you sure you want to delete this action?")) {
      setActionList(actionList.filter((action) => action.id !== actionId));
    }
  };

  const renderField = (action, field) => {
    const value = action.fields?.[field.var] ?? "";
    const width = field.width || 200;
    const actionErrors = errors[action.id] || {};

    const fieldError =
      showAllErrors || touched[`${action.id}.${field.var}`]
        ? actionErrors[field.var]
        : undefined;

    const commonSx = {
      minWidth: width,
      "& .MuiInputBase-input": { fontSize: "0.85rem" },
      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
      "& .MuiFormHelperText-root": { fontSize: "0.85rem" },
    };

    if (field.widget === "RadioButton") {
      return (
        <FormControl
          component="fieldset"
          error={!!fieldError}
          required={!!field.required}
          sx={{ ...commonSx, minHeight: 69 }}
          disabled={readOnly}
        >
          <FormLabel component="legend" sx={{ fontSize: "0.85rem", mb: 0.5 }}>
            {field.label}
          </FormLabel>
          <RadioGroup
            row
            value={value}
            onChange={(e) =>
              updateActionField(action.id, field.var, e.target.value)
            }
          >
            {field.values?.map((v) => (
              <FormControlLabel
                key={v}
                value={v}
                control={<Radio size="small" />}
                label={v}
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              />
            ))}
          </RadioGroup>
          {fieldError && (
            <Typography variant="caption" color="error">
              {fieldError}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (field.widget === "TextArea") {
      return (
        <TextField
          label={field.label}
          value={value}
          size="small"
          multiline
          minRows={field.rows ?? 3}
          maxRows={12}
          onChange={(e) =>
            updateActionField(action.id, field.var, e.target.value)
          }
          onBlur={() => markTouched(action.id, field.var)}
          required={!!field.required}
          sx={{ width: field.width ?? 400, ...commonSx }}
          error={!!fieldError}
          helperText={fieldError || " "}
          disabled={readOnly}
        />
      );
    }

    return (
      <TextField
        label={field.label}
        value={value}
        type={
          field.type === "float" || field.type === "int"
            ? "number"
            : field.type === "date"
              ? "date"
              : "text"
        }

        size="small"
        onChange={(e) =>
          updateActionField(action.id, field.var, e.target.value)
        }
        onBlur={() => markTouched(action.id, field.var)}
        required={!!field.required}
        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
        sx={{ width: field.width ?? 200, ...commonSx }}
        error={!!fieldError}
        helperText={fieldError || " "}

        disabled={readOnly}
      />
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "1rem", fontWeight: 600, mb: 1 }}
        >
          Action List
        </Typography>

        {!readOnly && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            {AVAILABLE_ACTIONS.map((actionType) => {
              const ICONS = {
                "Arrange Monitoring or Follow up": (
                  <FollowUpIcon fontSize="small" />
                ),
                "File Report": <FileIcon fontSize="small" />,
                "Communicate with Patient": (
                  <CommunicateIcon fontSize="small" />
                ),
                "Escalate or Urgent": <EscalateIcon fontSize="small" />,
                "Diary Entry": <DiaryIcon fontSize="small" />,
              };

              const colors = ACTION_COLORS[actionType] || {
                bg: "#fafafa",
                border: "#ddd",
                icon: "inherit",
                hoverBg: "#f0f0f0",
                hoverBorder: "#ccc",
              };

              const config = ACTION_CONFIGS[actionType];
              const used =
                !config.allowMultiple &&
                actionList.some((a) => a.node === actionType);

              return (
                <Paper
                  key={actionType}
                  onClick={() => {
                    if (used) return;
                    const newAction = {
                      id: `action_${Date.now()}_${Math.random()
                        .toString(36)
                        .substr(2, 9)}`,
                      node: actionType,
                      fields: {},
                    };
                    setActionList((prev) => [...prev, newAction]);
                  }}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 2,
                    backgroundColor: colors.bg,
                    pointerEvents: has_write && !used ? "auto" : "none",
                    opacity: has_write && !used ? 1 : 0.4,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: colors.hoverBg,
                      borderColor: colors.hoverBorder,
                    },
                  }}
                  elevation={0}
                >
                  <Box sx={{ color: colors.icon, display: "flex" }}>
                    {ICONS[actionType]}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.85rem", fontWeight: 500 }}
                  >
                    {actionType}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      {actionList.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            backgroundColor: "#f5f5f5",
            border: "1px dashed #ccc",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            No actions configured. Click an Action Button to get started.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {actionList.map((action, index) => {
            const config = ACTION_CONFIGS[action.node];
            if (!config) return null;

            return (
              <Paper
                key={action.id}
                sx={{
                  p: 1.5,
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  pointerEvents: has_write ? "auto" : "none",
                }}
                elevation={1}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 28,
                      fontSize: "0.8rem",
                    }}
                  >
                    {index + 1}
                  </Button>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "0.85rem", fontWeight: 500 }}
                  >
                    {action.node}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {!readOnly && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteAction(action.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {config.fields_config
                    ?.filter(
                      (field) =>
                        !field.visibleWhen ||
                        field.visibleWhen(action.fields || {}),
                    )
                    .map((field) => (
                      <Box
                        key={field.var}
                        sx={{
                          flexBasis:
                            field.widget === "RadioButton" ||
                            field.widget === "TextArea"
                              ? "100%"
                              : "auto",
                        }}
                      >
                        {renderField(action, field)}
                      </Box>
                    ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ActionListEditor;
